/*** IMPORTS FROM imports-loader ***/
var define = false;

/*jslint browser: true */

"use strict";

var ko = require("knockout");

var pli = require("../forms");
var disposeObject = require("../util/disposable").disposeObject;


//-------------------------------------------
function disposeGridItem(item) {
    var owner = item.extras.owner;
    item.extras.owner = null; // if we didn't remove this property, disposeObject would throw stack overflow exception
    disposeObject(item, true); // force-dispose (include "function" containers, i.e. "extras")
    item.extras.owner = owner;

    return item;
}


//-------------------------------------------
function arrayFirstIndexOf(array, predicate, predicateOwner) {
    var i, j;
    for (i = 0, j = array.length; i < j; i += 1) {
        if (predicate.call(predicateOwner, array[i])) {
            return i;
        }
    }
    return -1;
}

//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// GridViewModelFactory class
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

//-------------------------------------------
// Constructor (expects "new").
function GridViewModelFactory(factories, ItemClass, itemComparer, apiVersion) {
    if (!(this instanceof GridViewModelFactory)) { // if new was not used, force it now
        return new GridViewModelFactory(factories, ItemClass, itemComparer, apiVersion);
    }

    if (typeof factories !== "object") { throw new Error("factories was expected to be an object"); }
    if (typeof ItemClass !== "function") { throw new Error("ItemClass was expected to be a constructor function"); }

    this.apiVersion = (apiVersion || 1);
    this.factories = factories;
    this.ItemClass = ItemClass;
    this.itemComparer = (typeof itemComparer === "function"
        ? itemComparer
        : ItemClass.prototype.itemComparer === null || typeof ItemClass.prototype.itemComparer === "function"
            ? ItemClass.prototype.itemComparer
            : GridViewModelFactory.prototype._defaultItemComparer
    );

    this.navigationInitialized = ko.observable(false);
    this.gridPage = null; // will be initialized in "initializeNavigation"
    this.detailsPage = null; // -||-
    this.baseHash = ko.computed(GridViewModelFactory.prototype._computeBaseHash, this);
}


//-------------------------------------------
// Close currently opened "item details" page.
//
// Will be attached to observableArray instances
// created by "GridViewModelFactory.prototype.createObservableArray",
// i.e. expects "this" to be an observable array.
GridViewModelFactory.prototype._closeCurrentItem = function closeCurrentItem() {
    this.factory.detailsPage.form.close();
};


//-------------------------------------------
// Compute display ID for "this" item.
//
// Will be attached to instances of ItemClass,
// i.e. expects "this" to be an item.
GridViewModelFactory.prototype._computeDisplayId = function computeDisplayId() {
    var safeId, result = this.id();
    if (!result) {
        safeId = this.extras.safeId().substring(1); // safeId for items with falsey "id()" will be ".", ".(2)", ".(3)", etc -- substring will cut off the dot
        result = "(New";
        if (safeId) {
            result = result.concat(" ", safeId, ")");
        } else {
            result = result.concat(")");
        }
    }
    return result;
};


//-------------------------------------------
// Compute URL hash for "this" item.
//
// Will be attached to instances of ItemClass,
// i.e. expects "this" to be an item.
GridViewModelFactory.prototype._computeItemHash = function computeItemHash() {
    var itemHash, factory = this.extras.owner.factory;
    if (factory.gridPage) {
        if (factory.gridPage.defaultChildFormId === factory.detailsPage.id) {
            itemHash = factory.baseHash().concat("/", encodeURIComponent(this.extras.safeId()));
        } else {
            itemHash = factory.baseHash().concat("/", factory.detailsPage.id, ":", encodeURIComponent(this.extras.safeId()));
        }
    } else {
        factory.navigationInitialized(); // subscribe to "navigationInitialized" observable so that this computed observable is recomputed when factory is initialized
        itemHash = "#";
    }
    return itemHash;
};


//-------------------------------------------
// Create new "item" object for "this"
// observable array.
//
// Will be attached to observableArray instances
// created by "GridViewModelFactory.prototype.createObservableArray",
// i.e. expects "this" to be an observable array
// that owns the item.
GridViewModelFactory.prototype._createItem = function createItem(record) {
    // step 1: make new instance of item
    var apiVersion = this.factory.apiVersion;
    var i = (apiVersion > 1
        ? new this.factory.ItemClass(this.factory.factories, this) // factories + owner
        : new this.factory.ItemClass(record, this.factory.factories) // deprecated
    );
    if (!ko.isObservable(i.id)) {
        throw new Error("GridViewModel requires items to define an observable property \"id\"");
    }

    // step 2: populate item with data (if valid record)
    if (this.factory.apiVersion > 1 && record !== undefined && record !== null && typeof i.loadViewModel === "function") {
        i.loadViewModel(record);
    }

    // step 3: define system properties and computeds
    if (!i.hasOwnProperty("extras")) {
        Object.defineProperty(i, "extras", { enumerable: false, value: {} });
    }
    i.extras.owner = this;
    i.extras.safeId = ko.observable(i.id() || ".");
    i.extras.hash = ko.computed(GridViewModelFactory.prototype._computeItemHash, i);
    i.extras.displayId = ko.computed(GridViewModelFactory.prototype._computeDisplayId, i);

    // step 4: complete item creation
    if (this.factory.apiVersion > 1) {
        if (typeof i.completeViewModel === "function") {
            i.completeViewModel(record);
        }
    } else {
        if (typeof i.created === "function") {
            i.created();
        }
    }

    return i;
};


//-------------------------------------------
// Simple case-insensitive ID comparer.
// Used as a default comparer when ItemClass
// does not define a comparer.
//
// Does not use "this".
GridViewModelFactory.prototype._defaultItemComparer = function defaultItemComparer(left, right) {
    var leftId = ((left && left.id()) || "").toLowerCase(),
        rightId = ((right && right.id()) || "").toLowerCase();
    return leftId < rightId ? -1 : leftId > rightId ? 1 : 0;
};


//-------------------------------------------
// Will be attached to observableArray instances
// created by "GridViewModelFactory.prototype.createObservableArray",
// i.e. expects "this" to be an observable array.
GridViewModelFactory.prototype._deleteAndCloseCurrentItem = function deleteAndCloseCurrentItem() {
    if (ko.unwrap(this.disableDelete)) {
        return;
    }

    var current = this.current();
    this.remove(current);
    disposeGridItem(current);
    this.closeCurrent();
};


//-------------------------------------------
// Will be attached to observableArray instances
// created by "GridViewModelFactory.prototype.createObservableArray",
// i.e. expects "this" to be an observable array.
GridViewModelFactory.prototype._disposeObservableArray = function disposeObservableArray() {
    var i, l, a = this();
    for (i = 0, l = a.length; i < l; i += 1) {
        disposeGridItem(a[i]);
    }
};


//-------------------------------------------
// Computes total errors count for all items
// currently included in "this" array. Expects
// each item to have an observable "extras.errorsCount()"
// and expects "this" to be an observable array.
GridViewModelFactory.prototype._errorsCount = function gridErrorsCount() {
    var i, l, o,
        sum = 0,
        a = this();
    for (i = 0, l = a.length; i < l; i += 1) {
        o = a[i];
        if (o && o.extras && o.extras.errorsCount) {
            sum += o.extras.errorsCount();
        }
    }
    return sum;
};


//-------------------------------------------
// Load items into "this" observable array.
//
// Will be attached to observableArray instances
// created by "GridViewModelFactory.prototype.createObservableArray",
// i.e. expects "this" to be an observable array.
GridViewModelFactory.prototype._loadItems = function loadItems(records) {
    var i, l, item,
        loadMethodName = (this.factory.apiVersion > 1 ? "loadViewModel" : "loaded"),
        o = this(),
        oLength = o.length,
        recordsLength = (records ? records.length : 0),
        currentItemSafeId = this.current().extras.safeId(),
        currentItemIndex = ko.utils.arrayIndexOf(o, this.current());

    // start loading items
    this._isLoading(true);

    // update existing items
    for (i = 0, l = Math.min(recordsLength, oLength); i < l; i += 1) {
        o[i][loadMethodName](records[i]);
    }

    // create new items (if any)
    for (l = recordsLength; i < l; i += 1) {
        o.push(this.createItem(records[i]));
    }

    // dispose old items (if any)
    for (l = oLength - i, i = 0; i < l; i += 1) {
        item = o.pop();
        // if (window.console && window.console.group) { window.console.log("Disposing:", item); }
        disposeGridItem(item);
    }

    // end loading items
    // forces calculation safeId and hash
    this._isLoading(false);

    // if current item index changed - set new element as current
    if (currentItemIndex >= 0) {
        i = arrayFirstIndexOf(o, function (el) { return el.extras.safeId() === currentItemSafeId; });
        // index changed
        if (i !== currentItemIndex) {
            if (i >= 0) {
                // if item with the current safeId found select it
                this.current(o[i]);
            } else {
                // items don't contain item with current safeId
                // set current to default empty item
                this.current(this.factory.ItemClass.defaultEmptyItem);
                // close current
                this.closeCurrent();
            }
        }
    }

    // if there were changes to the underlying array, notify subscribers
    if (oLength !== o.length) {
        this.valueHasMutated();
    }

    return this;
};


//-------------------------------------------
// Create a new item, add it to viewModel
// and navigate to the newly created item
// in browser.
//
// Will be attached to observableArray instances
// created by "GridViewModelFactory.prototype.createObservableArray",
// i.e. expects "this" to be an observable array.
GridViewModelFactory.prototype._openNewItem = function openNewItem() {
    if (ko.unwrap(this.disableNew)) {
        return;
    }

    var o = this.createItem(),
        a = this();
    a.push(o);
    // does not notify subscribers that there is a new item, so force sort to send notification
    this.sort(null, a, true);
    this.factory.detailsPage.$focus = null; // reset details page focus to default
    location.hash = o.extras.hash();
    pli.forms.onHashChange();
};


//-------------------------------------------
// Will be attached to observableArray instances
// created by "GridViewModelFactory.prototype.createObservableArray",
// i.e. expects "this" to be an observable array.
GridViewModelFactory.prototype._openNextItem = function openNextItem() {
    var o = this.next();
    if (o) {
        location.hash = o.extras.hash();
    }
};


//-------------------------------------------
// Will be attached to observableArray instances
// created by "GridViewModelFactory.prototype.createObservableArray",
// i.e. expects "this" to be an observable array.
GridViewModelFactory.prototype._openPreviousItem = function openPreviousItem() {
    var o = this.previous();
    if (o) {
        location.hash = o.extras.hash();
    }
};


//-------------------------------------------
// If newItems are specified, they are converted
// into an array (if not already), then sorted,
// then set as value of "this" observable array
// (generating a single subscribers notification).
// Otherwise current observable array items are
// sorted (in place, generating a single subscribers
// notification).
// If no item comparer is specified, default
// comparer (as defined by factory that created
// this observable array) will be used.
// Will be attached to observableArray instances
// created by "GridViewModelFactory.prototype.createObservableArray",
// i.e. expects "this" to be an observable array.
GridViewModelFactory.prototype._sort = function sort(itemComparer, newItems, forceNotify) {
    if (typeof itemComparer !== "function") {
        itemComparer = this.factory.itemComparer; // could be null
    }

    var a = this.peek();
    if (typeof newItems === "object" && newItems !== null && newItems !== a) {
        if (itemComparer) {
            newItems = newItems.sort(itemComparer);
        }
        if (newItems !== a) {
            this(newItems);
            a = newItems;
        }
    } else {
        if (itemComparer) {
            // only sort if unsorted, to ensure that viewing sorted data does not trigger the dirty flag
            var sorted = true,
                index;
            for (index = 1; index < a.length; index += 1) {
                if (itemComparer(a[index - 1], a[index]) > 0) {
                    sorted = false;
                    break;
                }
            }
            if (!sorted) {
                this.valueWillMutate();
                a = a.sort(itemComparer);
                forceNotify = true;
            }
        }

        if (forceNotify) {
            this.valueHasMutated();
        }
    }

    return a;
};


//-------------------------------------------
// Compute base hash for "this" factory.
GridViewModelFactory.prototype._computeBaseHash = function computeBaseHash() {
    var gridPage = this.gridPage, crumbs, c;
    if (gridPage) {
        crumbs = pli.forms.crumbs();
        c = crumbs.end;
        while (c) {
            if (c.page === gridPage) {
                return c.hash;
            }
            c = c.previous;
        }
    } else { // grid page can only be falsy if factory has not been initialized yet
        this.navigationInitialized(); // subscribe to "navigationInitialized" observable so that this computed observable is recomputed when factory is initialized
    }

    return (this.baseHash ? this.baseHash.peek() : "#"); // if we can't determine current baseHash (and we determined it before), use last valid value
};


//-------------------------------------------
// Create a new observable array.
//
// options.disableNew and options.disableDelete can be
// truthy/falsy or an observable. If undefined, they
// will default to false. If disableNew is truthy, calling
// openNew does nothing, otherwise calling openNew
// behaves normally. If disableDelete is truthy, calling
// deleteAndCloseCurrent does nothing, otherwise
// calling deleteAndCloseCurrent behaves normally.
//
// Will be attached to GridViewModelFactory,
// i.e. expects "this" to be a factory.
GridViewModelFactory.prototype.createObservableArray = function createObservableArray(options) {
    var a = ko.observableArray();

    options = options || {};

    // direct functions and properties
    a.closeCurrent = GridViewModelFactory.prototype._closeCurrentItem.bind(a);
    a.createItem = GridViewModelFactory.prototype._createItem;
    a.deleteAndCloseCurrent = GridViewModelFactory.prototype._deleteAndCloseCurrentItem.bind(a);
    a.disableDelete = options.disableDelete;
    a.dispose = GridViewModelFactory.prototype._disposeObservableArray;
    a.factory = this;
    if (this.apiVersion > 1) {
        a.loadViewModel = GridViewModelFactory.prototype._loadItems;
    } else {
        a.load = GridViewModelFactory.prototype._loadItems;
    }
    a.openNew = GridViewModelFactory.prototype._openNewItem.bind(a);
    a.disableNew = options.disableNew;
    a.openNext = GridViewModelFactory.prototype._openNextItem.bind(a);
    a.openPrevious = GridViewModelFactory.prototype._openPreviousItem.bind(a);
    a.sort = GridViewModelFactory.prototype._sort;

    // knockout properties
    a.current = ko.observable(null);
    a.next = ko.observable(null);
    a.previous = ko.observable(null);
    a.errorsCount = ko.computed(GridViewModelFactory.prototype._errorsCount, a);

    // notify item class (if requested)
    var initGridArray = this.ItemClass.prototype && this.ItemClass.prototype.initGridArray;
    if (typeof initGridArray === "function") {
        initGridArray.call(this.ItemClass.prototype, a);
    }

    // if not yet created, create default empty item (disposed)
    if (!this.ItemClass.defaultEmptyItem) {
        this.ItemClass.defaultEmptyItem = disposeGridItem(a.createItem()); // we dispose the default item because we don't want it recomputing observables; it is only a placeholder for bindings that are using "current" observable
        this.ItemClass.defaultEmptyItemJson = ko.toJSON(this.ItemClass.defaultEmptyItem);
    }
    a.current(this.ItemClass.defaultEmptyItem);
    a._isLoading = ko.observable(false);

    return a;
};


//-------------------------------------------
// Important: "itemsObservable" is expected to
// be an observable that resolves to
// (its value is) "observableArray".
//
// Will be attached to GridViewModelFactory,
// i.e. expects "this" to be a factory.
GridViewModelFactory.prototype.initializeNavigation = function initializeGridNavigation(gridPage, detailsPage, itemsObservable) {
    if (typeof gridPage !== "object" || gridPage.type !== "PLI navigation page") { throw new Error("gridPage was expected to be a valid navigation page"); }
    if (typeof detailsPage !== "object" || detailsPage.type !== "PLI navigation page" || typeof detailsPage.form !== "object") { throw new Error("detailsPage was expected to be a valid navigation (form) page"); }
    if (!ko.isObservable(itemsObservable)) { throw new Error("itemsObservable was expected to be an observable"); }

    // Remember pages as properties in factory object
    // (in case caller ever needs them)
    this.gridPage = gridPage;
    this.detailsPage = detailsPage;

    // Local variables (used in closures below)
    var activeDetailsSafeId = null; // Remember safeId of the last item that was successfully activated via navigation

    // Find data for details form when it is
    // being activated.
    detailsPage.$page.on("pliInitializingFormData", function initializeDetailsData(event) {
        var a, i, l, item,
            items = itemsObservable(),
            id = event.pli.data.id.toLowerCase();
        for (a = items(), i = 0, l = a.length; i < l; i += 1) {
            item = a[i];
            if (item.extras.safeId().toLowerCase() === id) {
                activeDetailsSafeId = item.extras.safeId();
                event.pli.data.value = true;
                items.current(item);
                items.previous(i === 0 ? null : a[i - 1]);
                items.next(i === (l - 1) ? null : a[i + 1]);
                break;
            }
        }
    });

    // When deactivating details form, sort all items
    // (because some properties that affect sort order
    // might have been edited in the form);
    // then find index of the current item and make sure
    // we update saved page focus to reference correct
    // <a>nchor element
    detailsPage.$page.on("pliDeactivated", function deactivateDetails() {
        var $found, $anchors, i, l,
            items = itemsObservable(),
            current = items.current(),
            currentHash = current.extras.hash(),
            currentJson = ko.toJSON(current);

        var crumb = pli.forms.crumbs().begin;

        while (crumb.next) {
            crumb = crumb.next;
        }

        if (crumb.hash.lastIndexOf(currentHash, 0) !== 0) {
            activeDetailsSafeId = null;
            if (currentJson === items.factory.ItemClass.defaultEmptyItemJson) {
                if (window.console) { window.console.log("Deactivating empty!"); }
                items.remove(current);
                disposeGridItem(current);
                current = null;
            }
        }

        items.sort();

        if (current) {
            $anchors = gridPage.$page.find("a");
            for (i = 0, l = $anchors.length; i < l; i += 1) {
                $found = $anchors.eq(i);
                if ($found.attr("href") === currentHash) {
                    break;
                }
                $found = null;
            }
        } else {
            $found = null;
        }
        if ($found && $found.length) {
            gridPage.$focus = $found;
        } else {
            delete gridPage.$focus;
        }
    });

    // Ensure hash is synchronized with ID
    // of the currently selected grid item
    ko.computed(function () {
        var items = itemsObservable();

        // don't update hash if items are loading
        if (items._isLoading()) { return; }

        // get current item safeId value
        var safeId = items.current().extras.safeId();

        if (activeDetailsSafeId !== null && activeDetailsSafeId.toLowerCase() !== safeId.toLowerCase()) { // only update hash if details page is active and entered ID differs from hash ID (case-insensitive)
            detailsPage.form.open({
                id: safeId,
                replaceId: true,
                replacePreservesCrumbsTail: true,
                includeInHash: true,
                doNotUpdateHistory: true
            });
            pli.forms.onHashChange(); // make sure crumbs are updated at this point

            // Reset the value for the case when the hash changes *for the current
            // form*. This can happen when a form value that contributes to
            // safeId is changed; the hash changes but it does not result in a
            // new `initializingFormData` event, so `activeDetailsSafeId` will not
            // have its value reset.
            activeDetailsSafeId = safeId;
        }
    });

    // Ensure safe IDs are recomputed when
    // items collection changes or ID of any
    // item in this collection changes
    ko.computed(function () {
        var items = itemsObservable();

        // don't update safeId if items are loading
        if (items._isLoading()) { return; }

        var a = items();
        var known = {};

        var i, l, safeId, lowercaseId, count;
        for (i = 0, l = a.length; i < l; i += 1) {
            safeId = a[i].id() || ".";
            lowercaseId = safeId.toLowerCase(); // counting for safeId is case-insensitive, e.g. "bpo", "Bpo(2)", "BPO(3)"
            count = (known[lowercaseId] || 0) + 1;
            if (count > 1) {
                safeId = safeId.concat("(", count, ")");
            }
            known[lowercaseId] = count;
            a[i].extras.safeId(safeId);
        }
    });

    // Trigger recomputation of baseHash values
    // that were computed before this initialization
    this.navigationInitialized(true);
    this.navigationInitialized = null; // release for GC
};


module.exports = GridViewModelFactory;




//////////////////
// WEBPACK FOOTER
// ./~/pli/forms-viewmodel/gridViewModel.js
// module id = 470
// module chunks = 49